import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { ItemAvailability, Product, Review, WithContext } from 'schema-dts';
import { helmetJsonLdProp } from 'react-schemaorg';
import {
  ProductAttributesType,
  ProductType,
  ProductVariantsAttributesType,
  ProductVariantType,
} from 'types/ProductPageV2/Products';
import {
  ProductReviewsDetailsType,
  ProductReviewsRatingType,
} from 'types/ProductPageV2/ProductReviews';
import {
  getCurvyImages,
  getCurvyVideo,
  getStandardImages,
  getStandardVideo,
  getDescription,
  getRealSku,
  getEan,
  getBrandName,
  getUrl,
  getFinalPrice,
  getProductAvailabilityStatus,
  getFullName,
} from 'services/product';
import { getUri } from 'services/generic';
import useEnv from 'hooks/useEnv';

type Props = {
  product: ProductType | ProductVariantType;
  reviews?: ProductReviewsDetailsType[];
  rating?: ProductReviewsRatingType;
};

type ProductAttrType = ProductAttributesType & ProductVariantsAttributesType;

const ProductJsonLd = ({
  product,
  reviews,
  rating,
}: Props): JSX.Element | null => {
  const productAttribute = product.Attributes as ProductAttrType;
  const MAX_RATING_POSSIBLE = 5;
  const MIN_RATING_POSSIBLE = 1;
  const { t } = useTranslation();
  const {
    baseUrlTwicpicsMedia,
    urlTwicpicsMedia,
    urlTwicpicsVideo,
    priceCurrency,
  } = useEnv();

  const getProductReviews = (reviews: ProductReviewsDetailsType[]) => {
    let bestRating = 1,
      worstRating = 5;

    for (const review of reviews) {
      bestRating = Math.max(bestRating, +review.rate);
      worstRating = Math.min(worstRating, +review.rate);
    }

    return {
      reviews: reviews.slice(0, 10),
      bestRating,
      worstRating,
    };
  };

  const getMedias = (baseMediaUrl: string, baseVideoUrl: string) => {
    const getMediaPath = (path: string) => baseMediaUrl + path;
    const getVideoPath = (path: string) => baseVideoUrl + path;

    const standardImages = getStandardImages(productAttribute, getMediaPath);
    const standardVideo = getStandardVideo(productAttribute, getVideoPath);
    const curvyImages = getCurvyImages(productAttribute, getMediaPath);
    const curvyVideo = getCurvyVideo(productAttribute, getVideoPath);

    return [
      ...standardImages,
      ...(standardVideo ? [standardVideo] : []),
      ...curvyImages,
      ...(curvyVideo ? [curvyVideo] : []),
    ].filter(Boolean);
  };

  const getStockStatus = (availability: string): ItemAvailability => {
    const stockStatusMapping: Record<string, ItemAvailability> = {
      [t('UNAVAILABLE').toLowerCase()]: 'https://schema.org/OutOfStock',
    };

    return (
      stockStatusMapping[availability.toLowerCase()] ||
      'https://schema.org/InStock'
    );
  };

  const images = getMedias(
    baseUrlTwicpicsMedia + urlTwicpicsMedia,
    baseUrlTwicpicsMedia + urlTwicpicsVideo
  );

  const productName = getFullName(productAttribute);
  const jsonLdObj: WithContext<Product> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: productName,
    image: images.length > 0 ? images : undefined,
    description: getDescription(productAttribute),
    sku: getRealSku(productAttribute),
    gtin13: getEan(productAttribute) || undefined,
    brand: {
      '@type': 'Brand',
      name: getBrandName(productAttribute) || 'Afibel',
    },
  };

  const url = getUrl(product, (url: string) => {
    return url
      ? window.location.protocol + '//' + window.location.host + getUri(url)
      : '';
  });

  const finalPrice = getFinalPrice(productAttribute);
  const stockStatus = getStockStatus(
    getProductAvailabilityStatus(productAttribute)
  );

  if (url && finalPrice && stockStatus) {
    jsonLdObj.offers = {
      '@type': 'Offer',
      url: url,
      priceCurrency: priceCurrency,
      price: finalPrice,
      availability: stockStatus,
      itemCondition: 'https://schema.org/NewCondition',
    };
  }

  const { reviewsLd: reviewsJsonLd } = useMemo(() => {
    const result = getProductReviews(reviews || []),
      hasResult = result.reviews.length > 0;

    return {
      reviewsLd: result.reviews.map(
        (review: ProductReviewsDetailsType): Review => {
          return {
            '@type': 'Review',
            author: {
              '@type': 'Person',
              name: review.firstname,
            },
            datePublished: review.publish_date,
            reviewBody: review.review,
            reviewRating: {
              '@type': 'Rating',
              bestRating: MAX_RATING_POSSIBLE,
              ratingValue: +review.rate,
              worstRating: MIN_RATING_POSSIBLE,
            },
          };
        }
      ),
      bestRating: hasResult ? result.bestRating : 0,
      worstRating: hasResult ? result.worstRating : 0,
    };
  }, [reviews]);

  if (reviewsJsonLd && rating) {
    jsonLdObj.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: +rating.rate,
      ratingCount: +rating.count,
      bestRating: MAX_RATING_POSSIBLE,
      worstRating: MIN_RATING_POSSIBLE,
      reviewCount: +rating.count,
      itemReviewed: {
        '@type': 'IndividualProduct',
        name: productName,
      },
    };

    jsonLdObj.review = reviewsJsonLd;
  }

  return <Helmet script={[helmetJsonLdProp<Product>(jsonLdObj)]} />;
};

export default React.memo(ProductJsonLd);
